import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { brands as seedBrands } from '../data/brands';
import { vehicles as seedVehicles } from '../data/vehicles';
import { banners as seedBanners } from '../data/banners';
import { defaultSettings } from '../data/options';
import { Brand, Vehicle } from '../types/catalog';
import { Banner, ChangeLog, Settings } from '../types/admin';
import { Lead, LeadStatus, NewLead } from '../types/lead';
import { SheetStatus, useSheetCatalog } from '../hooks/useSheetCatalog';
import { fallbackImages, mergeSheetCatalog } from '../utils/sheetsCatalog';
import { enqueueLead, flushLeadOutbox } from '../utils/leadSync';
import { captureAttribution } from '../utils/attribution';
export interface CatalogSync {
  status: SheetStatus;
  lastSync: string | null;
  error: string | null;
  /** Cantidad de unidades publicadas que llegan desde la planilla. */
  sheetCount: number;
  refresh: () => void;
}
interface StoreData {
  brands: Brand[];
  vehicles: Vehicle[];
  banners: Banner[];
  leads: Lead[];
  changes: ChangeLog[];
  settings: Settings;
  seedVersion?: number;
}
const SEED_VERSION = 4;
// Unidades de ejemplo que se suman a navegadores con datos ya guardados al subir de versión.
const SEED_NEW_VEHICLES: Record<number, string[]> = {
  3: ['jeep-renegade-sport', 'jeep-compass-sport', 'jeep-commander-limited', 'ram-rampage-rebel', 'ram-dakota-laramie', 'citroen-c3-feel']
};
interface StoreValue extends StoreData {
  /** Unidades guardadas sólo en este navegador (ejemplos y altas del panel actual). */
  localVehicles: Vehicle[];
  catalogSync: CatalogSync;
  compareIds: string[];
  toggleCompare: (id: string) => 'added' | 'removed' | 'full';
  setCompareIds: (ids: string[]) => void;
  updateVehicle: (id: string, patch: Partial<Vehicle>) => void;
  createVehicle: (v: Vehicle) => void;
  deleteVehicle: (id: string) => void;
  updateBrand: (id: string, patch: Partial<Brand>) => void;
  createBrand: (b: Brand) => void;
  saveBanner: (b: Banner) => void;
  deleteBanner: (id: string) => void;
  addLead: (input: NewLead) => Lead;
  updateLead: (id: string, update: {
    status?: LeadStatus;
    note?: string;
    assignedTo?: string;
  }) => void;
  deleteLead: (id: string) => void;
  updateSettings: (patch: Partial<Settings>) => void;
  resetData: () => void;
}
const STORAGE_KEY = 'vcars-store-v1';
const COMPARE_KEY = 'vcars-compare-v1';
const StoreContext = createContext<StoreValue | null>(null);
function seed(): StoreData {
  return {
    brands: seedBrands,
    vehicles: seedVehicles,
    banners: seedBanners,
    leads: [],
    changes: [],
    settings: defaultSettings,
    seedVersion: SEED_VERSION
  };
}
function loadData(): StoreData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seed();
    const parsed = JSON.parse(raw) as Partial<StoreData>;
    const stored = parsed.brands ?? [];
    const brands = [...stored, ...seedBrands.filter(b => !stored.some(s => s.id === b.id))];
    // Si la versión de contenido inicial cambió, se actualizan los banners de base.
    const fromVersion = parsed.seedVersion ?? 1;
    const outdated = fromVersion < SEED_VERSION;
    const storedVehicles = parsed.vehicles ?? seedVehicles;
    const newIds = Object.entries(SEED_NEW_VEHICLES).filter(([v]) => Number(v) > fromVersion).flatMap(([, ids]) => ids);
    const addedVehicles = seedVehicles.filter(v => newIds.includes(v.id) && !storedVehicles.some(s => s.id === v.id));
    return {
      ...seed(),
      ...parsed,
      vehicles: [...storedVehicles, ...addedVehicles],
      brands,
      banners: outdated ? seedBanners : parsed.banners ?? seedBanners,
      seedVersion: SEED_VERSION,
      settings: {
        ...defaultSettings,
        ...(parsed.settings ?? {}),
        whatsapp: parsed.settings?.whatsapp || defaultSettings.whatsapp
      }
    };
  } catch {
    return seed();
  }
}
function loadCompare(): string[] {
  try {
    const raw = localStorage.getItem(COMPARE_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, 3) : [];
  } catch {
    return [];
  }
}
const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const leadCode = () => `VC-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
const nowIso = () => new Date().toISOString();
function flatten(value: unknown, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  if (value === null || value === undefined) {
    out[prefix || 'valor'] = '—';
  } else if (Array.isArray(value)) {
    out[prefix] = value.length ? value.join(', ') : '—';
  } else if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (k === 'updatedAt') return;
      Object.assign(out, flatten(v, prefix ? `${prefix}.${k}` : k));
    });
  } else {
    out[prefix] = String(value);
  }
  return out;
}
function diff(entityType: ChangeLog['entityType'], entityId: string, entityLabel: string, before: unknown, after: unknown): ChangeLog[] {
  const a = flatten(before);
  const b = flatten(after);
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  const at = nowIso();
  return keys.filter(k => a[k] !== b[k]).map(k => ({
    id: uid(),
    at,
    entityType,
    entityId,
    entityLabel,
    field: k,
    from: a[k] ?? '—',
    to: b[k] ?? '—'
  }));
}
export function StoreProvider({
  children,
  showExamples = true
}: {
  children: React.ReactNode;
  showExamples?: boolean;
}) {
  const [data, setData] = useState<StoreData>(loadData);
  const [compareIds, setCompareIdsState] = useState<string[]>(loadCompare);
  const sheet = useSheetCatalog();

  // Origen de la visita y reenvío de consultas que quedaron pendientes por falta de conexión.
  useEffect(() => {
    captureAttribution();
    void flushLeadOutbox();
    const onOnline = () => void flushLeadOutbox();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);
  const merged = useMemo(() => sheet.data ? mergeSheetCatalog(sheet.data, data.brands) : {
    brands: data.brands,
    vehicles: [] as Vehicle[],
    photoOnly: {} as Record<string, string[]>
  }, [sheet.data, data.brands]);
  const catalogSync = useMemo<CatalogSync>(() => ({
    status: sheet.status,
    lastSync: sheet.lastSync,
    error: sheet.error,
    sheetCount: merged.vehicles.length,
    refresh: sheet.refresh
  }), [sheet.status, sheet.lastSync, sheet.error, sheet.refresh, merged.vehicles.length]);
  const vehicles = useMemo(() => {
    const exampleById = new Map(data.vehicles.map(v => [v.id, v]));
    // Unidades de la planilla: si no traen fotos, usan las del ejemplo con el mismo id.
    const fromSheet = merged.vehicles.map(v => v.images.length ? v : {
      ...v,
      images: exampleById.get(v.id)?.images ?? fallbackImages()
    });
    const sheetIds = new Set(fromSheet.map(v => v.id));
    // Ejemplos: si la planilla trae una fila con su id y sólo fotos, se reemplazan las fotos.
    const examples = showExamples ? data.vehicles.filter(v => !sheetIds.has(v.id)).map(v => merged.photoOnly[v.id] ? {
      ...v,
      images: merged.photoOnly[v.id]
    } : v) : [];
    return [...fromSheet, ...examples];
  }, [merged.vehicles, merged.photoOnly, data.vehicles, showExamples]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // almacenamiento lleno o bloqueado
    }
  }, [data]);
  useEffect(() => {
    try {
      localStorage.setItem(COMPARE_KEY, JSON.stringify(compareIds));
    } catch {
      // ignorar
    }
  }, [compareIds]);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setData(loadData());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const toggleCompare = useCallback((id: string): 'added' | 'removed' | 'full' => {
    if (compareIds.includes(id)) {
      setCompareIdsState(ids => ids.filter(x => x !== id));
      return 'removed';
    }
    if (compareIds.length >= 3) return 'full';
    setCompareIdsState(ids => [...ids, id]);
    return 'added';
  }, [compareIds]);
  const setCompareIds = useCallback((ids: string[]) => setCompareIdsState(ids.slice(0, 3)), []);
  const updateVehicle = useCallback((id: string, patch: Partial<Vehicle>) => {
    setData(d => {
      const prev = d.vehicles.find(v => v.id === id);
      if (!prev) return d;
      const at = nowIso();
      const next: Vehicle = {
        ...prev,
        ...patch,
        offer: patch.offer !== undefined ? patch.offer ? {
          ...patch.offer,
          updatedAt: at
        } : null : prev.offer,
        updatedAt: at
      };
      const brand = d.brands.find(b => b.id === next.brandId)?.name ?? '';
      const log = diff('vehiculo', id, `${brand} ${next.model} ${next.version}`, prev, next);
      return {
        ...d,
        vehicles: d.vehicles.map(v => v.id === id ? next : v),
        changes: [...log, ...d.changes]
      };
    });
  }, []);
  const createVehicle = useCallback((v: Vehicle) => {
    setData(d => {
      const brand = d.brands.find(b => b.id === v.brandId)?.name ?? '';
      const entry: ChangeLog = {
        id: uid(),
        at: nowIso(),
        entityType: 'vehiculo',
        entityId: v.id,
        entityLabel: `${brand} ${v.model} ${v.version}`,
        field: 'alta',
        from: '—',
        to: 'Unidad creada'
      };
      return {
        ...d,
        vehicles: [...d.vehicles, v],
        changes: [entry, ...d.changes]
      };
    });
  }, []);
  const deleteVehicle = useCallback((id: string) => {
    setData(d => {
      const v = d.vehicles.find(x => x.id === id);
      if (!v) return d;
      const entry: ChangeLog = {
        id: uid(),
        at: nowIso(),
        entityType: 'vehiculo',
        entityId: id,
        entityLabel: `${v.model} ${v.version}`,
        field: 'baja',
        from: 'Activa',
        to: 'Unidad eliminada'
      };
      return {
        ...d,
        vehicles: d.vehicles.filter(x => x.id !== id),
        changes: [entry, ...d.changes]
      };
    });
    setCompareIdsState(ids => ids.filter(x => x !== id));
  }, []);
  const updateBrand = useCallback((id: string, patch: Partial<Brand>) => {
    setData(d => {
      const prev = d.brands.find(b => b.id === id);
      if (!prev) return d;
      const at = nowIso();
      const next: Brand = {
        ...prev,
        ...patch,
        defaultOffer: {
          ...(patch.defaultOffer ?? prev.defaultOffer),
          updatedAt: patch.defaultOffer ? at : prev.defaultOffer.updatedAt
        }
      };
      const log = diff('marca', id, next.name, prev, next);
      return {
        ...d,
        brands: d.brands.map(b => b.id === id ? next : b),
        changes: [...log, ...d.changes]
      };
    });
  }, []);
  const createBrand = useCallback((b: Brand) => {
    setData(d => ({
      ...d,
      brands: [...d.brands, b],
      changes: [{
        id: uid(),
        at: nowIso(),
        entityType: 'marca',
        entityId: b.id,
        entityLabel: b.name,
        field: 'alta',
        from: '—',
        to: 'Marca creada'
      }, ...d.changes]
    }));
  }, []);
  const saveBanner = useCallback((b: Banner) => {
    setData(d => {
      const prev = d.banners.find(x => x.id === b.id);
      const log = prev ? diff('banner', b.id, b.title, prev, b) : [{
        id: uid(),
        at: nowIso(),
        entityType: 'banner' as const,
        entityId: b.id,
        entityLabel: b.title,
        field: 'alta',
        from: '—',
        to: 'Banner creado'
      }];
      return {
        ...d,
        banners: prev ? d.banners.map(x => x.id === b.id ? b : x) : [...d.banners, b],
        changes: [...log, ...d.changes]
      };
    });
  }, []);
  const deleteBanner = useCallback((id: string) => {
    setData(d => ({
      ...d,
      banners: d.banners.filter(b => b.id !== id)
    }));
  }, []);
  const addLead = useCallback((input: NewLead): Lead => {
    const at = nowIso();
    const lead: Lead = {
      ...input,
      id: uid(),
      code: input.code ?? leadCode(),
      createdAt: at,
      status: 'recibida',
      assignedTo: '',
      timeline: [{
        at,
        status: 'recibida',
        note: 'Consulta registrada'
      }]
    };
    setData(d => ({
      ...d,
      leads: [lead, ...d.leads]
    }));
    enqueueLead(lead);
    return lead;
  }, []);
  const updateLead = useCallback((id: string, update: {
    status?: LeadStatus;
    note?: string;
    assignedTo?: string;
  }) => {
    setData(d => ({
      ...d,
      leads: d.leads.map(l => {
        if (l.id !== id) return l;
        const status = update.status ?? l.status;
        const changed = update.status && update.status !== l.status;
        const note = update.note?.trim();
        return {
          ...l,
          status,
          assignedTo: update.assignedTo ?? l.assignedTo,
          timeline: changed || note ? [...l.timeline, {
            at: nowIso(),
            status,
            note: note || 'Cambio de estado'
          }] : l.timeline
        };
      })
    }));
  }, []);
  const deleteLead = useCallback((id: string) => {
    setData(d => ({
      ...d,
      leads: d.leads.filter(l => l.id !== id)
    }));
  }, []);
  const updateSettings = useCallback((patch: Partial<Settings>) => {
    setData(d => {
      const next = {
        ...d.settings,
        ...patch
      };
      const log = diff('ajustes', 'settings', 'Ajustes generales', {
        ...d.settings,
        adminPin: '•••'
      }, {
        ...next,
        adminPin: patch.adminPin ? '•••• (modificado)' : '•••'
      });
      return {
        ...d,
        settings: next,
        changes: [...log, ...d.changes]
      };
    });
  }, []);
  const resetData = useCallback(() => {
    setData(seed());
    setCompareIdsState([]);
  }, []);
  const value = useMemo<StoreValue>(() => ({
    ...data,
    brands: merged.brands,
    vehicles,
    localVehicles: data.vehicles,
    catalogSync,
    compareIds,
    toggleCompare,
    setCompareIds,
    updateVehicle,
    createVehicle,
    deleteVehicle,
    updateBrand,
    createBrand,
    saveBanner,
    deleteBanner,
    addLead,
    updateLead,
    deleteLead,
    updateSettings,
    resetData
  }), [data, merged.brands, vehicles, catalogSync, compareIds, toggleCompare, setCompareIds, updateVehicle, createVehicle, deleteVehicle, updateBrand, createBrand, saveBanner, deleteBanner, addLead, updateLead, deleteLead, updateSettings, resetData]);
  return <StoreContext.Provider value={value} data-id="element-1126" data-mp="1b75gvnear0el">{children}</StoreContext.Provider>;
}
export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore debe usarse dentro de StoreProvider');
  return ctx;
}